import { css } from 'styled-components';
import { color, responseFont } from '../styles/mixins';
import type { DefaultTheme } from './theme';

interface ThemeProps {
  theme: DefaultTheme;
}

type BentonSansStyle = 'paragraph' | 'label' | 'caps' | 'button' | 'link';
interface FontSizes {
  paragraph: 18 | 16 | 14 | 12;
  label: 18 | 16 | 14 | 12;
  caps: 14 | 12;
  button: 14;
  link: 18 | 16 | 14 | 12;
}

const fonts = {
  benton<T extends BentonSansStyle>(fontSize: FontSizes[T] = 14, style: T) {
    const lineHeights = {
      18: 26,
      16: 24,
      14: 22,
      12: 18,
    };

    let lineHeight = lineHeights[fontSize];
    let letterSpacing = 0;
    let fontWeight = 400;
    let textTransform = 'none';

    if (style === 'button') {
      lineHeight = 20;
      letterSpacing = 0.25;
    }

    if (style === 'caps' || style === 'button') {
      letterSpacing = 0.25;
    }

    if (style !== 'paragraph') {
      fontWeight = 500;
    }

    if (style === 'caps') {
      textTransform = 'uppercase';
    }

    return css`
      ${responseFont(fontSize, fontSize + 2)}
      font-family: ${(props) => props.theme.font.fontBentonSans};
      line-height: ${lineHeight / fontSize};
      font-weight: ${fontWeight};
      letter-spacing: ${letterSpacing}px;
      text-transform: ${textTransform};
    `;
  },
  mercury(fontSize: 32 | 28 | 24 | 20 | 18) {
    let letterSpacing = 0;

    if (fontSize === 20) {
      letterSpacing = 0.25;
    }

    const lineHeights = {
      32: 40,
      28: 32,
      24: 28,
      20: 24,
      18: 20,
    };
    const lineHeight = lineHeights[fontSize];
    return css`
      ${responseFont(fontSize, fontSize + 2)}
      font-family: ${(props) => props.theme.font.fontMercuryDisplay};
      font-weight: 500;
      line-height: ${lineHeight / fontSize};
      color: ${color('gray100')};
      letter-spacing: ${letterSpacing}px;
    `;
  },
};

export const m18 = fonts.mercury(18);

export const m20 = fonts.mercury(20);

export const m24 = fonts.mercury(24);

export const m28 = fonts.mercury(28);

export const m32 = fonts.mercury(32);

export const p12 = fonts.benton(12, 'paragraph');
export const p14 = fonts.benton(14, 'paragraph');
export const p16 = fonts.benton(16, 'paragraph');

export const label12 = fonts.benton(12, 'label');

export const label14 = fonts.benton(14, 'label');

export const label16 = fonts.benton(16, 'label');

export const button14 = (props: ThemeProps) => css`
  ${fonts.benton(14, 'button')};
`;

export const caps12 = (props: ThemeProps) => css`
  ${fonts.benton(12, 'caps')};
  color: ${color('gray80')};
`;

export const caps14 = (props: ThemeProps) => css`
  ${fonts.benton(14, 'caps')};
  color: ${color('gray100')};
`;

export const link14 = (props: ThemeProps) => css`
  ${fonts.benton(14, 'link')};
  color: ${color('blue60')};

  &:hover {
    text-decoration: underline;
  }
`;

export const typography = {
  m20,
  m24,
  m28,
  m32,
  caps12,
  caps14,
  p12,
  p14,
  p16,
  label12,
  label14,
  label16,
  button14,
  fonts,
};
